import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PublishQueueFieldsFragment } from '@/generated/graphql';
import { QueueAccountsFieldset, type QueueAccount } from './QueueAccountsFieldset';
import { QueueWeeklySchedule } from './QueueWeeklySchedule';
import { DEFAULT_WEEKLY_SLOT, parseWeeklySlots } from '@/lib/publishQueues/weeklySlots';
import { validateQueueDraft } from '@/lib/publishQueues/validateQueueDraft';
import { useSocialPostingPlatforms } from '@/hooks/use-social-posting-platforms';
import { isFacebookPublishableConnection } from '@/lib/publishing/resolvePublishEligibility';
import { isPublishPlatform } from '@/lib/publishing/types';
import type {
  MultiAccountMode,
  QueueDraft,
  SpacingScope,
  WeeklySlot,
} from '@/lib/publishQueues/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

type RadioCardOption<T extends string> = { value: T; label: string; hint: string };

function RadioCardGroup<T extends string>({
  legend, name, value, options, onChange,
}: {
  legend: string;
  name: string;
  value: T;
  options: RadioCardOption<T>[];
  onChange: (value: T) => void;
}) {
  return (
    <fieldset className="min-w-0 space-y-3">
      <legend className="text-sm font-medium">{legend}</legend>
      <RadioGroup
        value={value}
        onValueChange={(next) => onChange(next as T)}
        className="grid gap-3 sm:grid-cols-2"
      >
        {options.map((option) => (
          <Label
            key={option.value}
            htmlFor={`${name}-${option.value}`}
            className="flex cursor-pointer items-start gap-3 rounded-lg border p-3"
          >
            <RadioGroupItem id={`${name}-${option.value}`} value={option.value} aria-label={option.label} />
            <span>
              <span className="block">{option.label}</span>
              <span className="mt-1 block text-xs font-normal text-muted-foreground">{option.hint}</span>
            </span>
          </Label>
        ))}
      </RadioGroup>
    </fieldset>
  );
}

type QueueFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connections: QueueAccount[];
  initialQueue: PublishQueueFieldsFragment | null;
  submitting: boolean;
  onSubmit: (draft: QueueDraft) => Promise<void> | void;
};

export function QueueFormDialog({
  open,
  onOpenChange,
  connections: allConnections,
  initialQueue,
  submitting,
  onSubmit,
}: QueueFormDialogProps) {
  const { t } = useTranslation();
  const canPostToFacebook = useSocialPostingPlatforms().includes('facebook');
  const connections = useMemo(
    () => allConnections.filter((connection) => isPublishPlatform(connection.platform)
      && (connection.platform !== 'facebook'
        || (canPostToFacebook && isFacebookPublishableConnection({
          ...connection,
          scopes: Array.isArray(connection.scopes) ? connection.scopes : [],
        })))),
    [allConnections, canPostToFacebook],
  );
  const connectionIds = useMemo(() => new Set(connections.map(({ id }) => id)), [connections]);
  // Stored Facebook accounts the gate currently hides: kept on save, shown read-only.
  const hiddenStoredAccounts = useMemo(() => {
    const stored = initialQueue?.social_connection_ids ?? [];
    return stored
      .map((id) => allConnections.find((connection) => connection.id === id))
      .filter((connection): connection is QueueAccount => (
        !!connection && connection.platform === 'facebook' && !connectionIds.has(connection.id)
      ));
  }, [allConnections, connectionIds, initialQueue]);
  const defaultTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [],
  );
  const [name, setName] = useState('');
  const [selectedIds, setSelectedConnectionIds] = useState<string[]>([]);
  const selectedConnectionIds = selectedIds.filter((id) => connectionIds.has(id));
  const [mode, setMode] = useState<MultiAccountMode>('cross_post');
  const [gapMinutes, setGapMinutes] = useState('60');
  const [timezone, setTimezone] = useState(defaultTimezone);
  const [spacingScope, setSpacingScope] = useState<SpacingScope>('queue');
  const [slots, setSlots] = useState<WeeklySlot[]>([{ ...DEFAULT_WEEKLY_SLOT }]);
  const [validationCode, setValidationCode] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(initialQueue?.name ?? '');
    setSelectedConnectionIds(initialQueue?.social_connection_ids ?? []);
    setMode(initialQueue?.multi_account_mode === 'round_robin' ? 'round_robin' : 'cross_post');
    setGapMinutes(initialQueue ? String(initialQueue.min_gap_minutes) : '60');
    setTimezone(initialQueue?.timezone || defaultTimezone);
    setSpacingScope(initialQueue?.spacing_scope === 'account' ? 'account' : 'queue');
    const initialSlots = parseWeeklySlots(initialQueue?.weekly_slots);
    setSlots(initialSlots.length ? initialSlots.map((slot) => ({
      ...slot, endTimeLocal: slot.endTimeLocal ?? slot.timeLocal,
    })) : [{ ...DEFAULT_WEEKLY_SLOT }]);
    setValidationCode(null);
  }, [defaultTimezone, initialQueue, open]);

  const toggleConnection = (id: string, checked: boolean) => {
    setSelectedConnectionIds((current) => {
      if (checked) return current.includes(id) ? current : [...current, id];
      return current.filter((connectionId) => connectionId !== id);
    });
  };

  const moveConnection = (id: string, delta: -1 | 1) => {
    setSelectedConnectionIds((current) => {
      current = current.filter((connectionId) => connectionIds.has(connectionId));
      const from = current.indexOf(id);
      const to = from + delta;
      if (from < 0 || to < 0 || to >= current.length) return current;
      const next = [...current];
      [next[from], next[to]] = [next[to], next[from]];
      return next;
    });
  };

  const handleSubmit = async () => {
    const submittedIds = [...selectedConnectionIds];
    const storedIds = initialQueue?.social_connection_ids ?? [];
    for (const hidden of hiddenStoredAccounts) {
      submittedIds.splice(Math.min(storedIds.indexOf(hidden.id), submittedIds.length), 0, hidden.id);
    }
    const draft: QueueDraft = {
      name,
      weeklySlots: slots,
      socialConnectionIds: submittedIds,
      multiAccountMode: mode,
      minGapMinutes: gapMinutes.trim() ? Number(gapMinutes) : Number.NaN,
      timezone,
      spacingScope,
    };
    const validation = validateQueueDraft(draft);
    if (validation.ok === false) {
      setValidationCode(validation.code);
      return;
    }
    setValidationCode(null);
    await onSubmit(draft);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100%_-_2rem)] max-w-3xl grid-cols-[minmax(0,1fr)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t(initialQueue ? 'publishQueues.form.editTitle' : 'publishQueues.form.createTitle')}
          </DialogTitle>
          <DialogDescription>{t('publishQueues.form.description')}</DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-6 py-2">
          <div className="space-y-2">
            <Label htmlFor="queue-name">{t('publishQueues.form.name')}</Label>
            <Input
              id="queue-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('publishQueues.form.namePlaceholder')}
            />
          </div>

          <QueueAccountsFieldset connections={connections} selectedConnectionIds={selectedConnectionIds}
            onToggle={toggleConnection} onMove={moveConnection} />

          {hiddenStoredAccounts.length > 0 ? (
            <div data-testid="queue-hidden-accounts" className="space-y-2 rounded-lg border border-dashed p-3">
              <ul className="space-y-1 text-sm">
                {hiddenStoredAccounts.map((account) => (
                  <li key={account.id}>
                    Facebook: {account.display_name || account.username || account.id}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">{t('publishQueues.form.hiddenAccounts.note')}</p>
            </div>
          ) : null}

<RadioCardGroup<MultiAccountMode>
            legend={t('publishQueues.form.mode.label')}
            name="queue-mode"
            value={mode}
            onChange={setMode}
            options={[
              { value: 'cross_post', label: t('publishQueues.form.mode.crossPost'), hint: t('publishQueues.form.mode.crossPostHint') },
              { value: 'round_robin', label: t('publishQueues.form.mode.roundRobin'), hint: t('publishQueues.form.mode.roundRobinHint') },
            ]}
          />

          <div className="max-w-xs space-y-2">
            <Label htmlFor="queue-gap">{t('publishQueues.form.gapMinutes')}</Label>
            <Input id="queue-gap" type="number" min="5" step="5" value={gapMinutes}
              onChange={(event) => setGapMinutes(event.target.value)} />
            <p className="text-xs text-muted-foreground">{t('publishQueues.form.gapHint')}</p>
          </div>

          <RadioCardGroup<SpacingScope>
            legend={t('publishQueues.form.spacing.label')}
            name="queue-spacing"
            value={spacingScope}
            onChange={setSpacingScope}
            options={[
              { value: 'queue', label: t('publishQueues.form.spacing.queue'), hint: t('publishQueues.form.spacing.queueHint') },
              { value: 'account', label: t('publishQueues.form.spacing.account'), hint: t('publishQueues.form.spacing.accountHint') },
            ]}
          />

          <QueueWeeklySchedule timezone={timezone} onTimezoneChange={setTimezone}
            slots={slots} onSlotsChange={setSlots} />

          {validationCode ? (
            <p role="alert" className="text-sm text-destructive">
              {t(`publishQueues.validation.${validationCode}`)}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('publishQueues.form.cancel')}
          </Button>
          <Button type="button" disabled={submitting} onClick={() => void handleSubmit()}>
            {t(submitting ? 'publishQueues.form.saving' : 'publishQueues.form.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
